import React, { useCallback, useEffect, useState } from 'react';
import { DateSelector } from '../../components';
import SunIcon from '../../assets/SunIcon';
import ReusableButton from '../../components/common/Button/Button';
import AccordianDownIcon from '../../assets/AccordianDownIcon';
import {
  encodeDateForUrl,
  getAdjustedDate,
  validateDateRange,
} from '../../utils/dateUtils';
import viewBookingAPI from '../../api/viewBookingAPI';
import useLocationStore from '../../store/locatonStore';
import SearchIcon from '../../assets/SearchIcon';
import {
  bookingHeader,
  bookingHeaderKeyMapping,
} from '../../constants/headerConstants';
import useUserStore from '../../store/userStore';
import { generatePretravelLink } from '../../utils/generatePretravelLink';
import {
  allocation,
  defaultHiddenViewBookingColumns,
  locationConstant,
  setWithColumn,
  truncateColumn,
  viewBookingExcludedKeys,
} from '../../constants/constant';
import { translate } from '../../i18n';
import { ITableHeader } from '../../interfaces/tableHeader';
import { IVisibleColumns } from '../../interfaces/visibleColumns';
import useTemperatureStore from '../../store/temperatureStore';
import Loader from '../../assets/loader/Loader';
import { IAgent } from '../../type/agent';
import ReusableAutocomplete from '../../components/common/Autocomplete';
import { ITripStatus } from '../../type/tripStatus';
import useBgColorStore from '../../store/bgColor';
import CalendarHeader from '../../components/common/Header/CalenderHeader';
import SunIconDark from '../../assets/SunIconDark';
import { useClickOutside } from '../../hooks/useClickOutside';
import { getStatusColorClass } from '../../utils/utility';
import AccordianDownIconDark from '../../assets/AccordianDownIconDark';

const BookingsDesktop = () => {
  const user = useUserStore((state) => state.user);
  const { bgColor, updateBgColor } = useBgColorStore();
  const { selectedTemperature } = useTemperatureStore();
  const selectedLocation = useLocationStore((state) => state.selectedLocation);
  const [selectedToDate, setSelectedToDate] = useState<Date>(getAdjustedDate());
  const [selectedFromDate, setSelectedFromDate] =
    useState<Date>(getAdjustedDate());
  const [isToggleColumns, setIsToggleColumns] = useState<boolean>(false);
  const [viewBooking, setViewBooking] = useState<any>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [booking, setBooking] = useState<any>([]);
  const [headers] = useState<ITableHeader[]>(bookingHeader);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [agentList, setAgentList] = useState<IAgent[]>();
  const [tripStatus, setTripStatusList] = useState<ITripStatus[]>();

  const [selectedAgent, setSelectedAgent] = useState<IAgent | null>(null);
  const [selectedTripStatus, setSelectedTripStatus] =
    useState<ITripStatus | null>(null);

  // Reference for table container to sync the scrolls
  const [tableRef, setTableRef] = useState<HTMLDivElement | null>(null);
  const [topScrollRef, setTopScrollRef] = useState<HTMLDivElement | null>(null);

  const keyMapping = bookingHeaderKeyMapping;

  const defaultVisibleColumns = headers.reduce<IVisibleColumns>(
    (acc, header) => {
      const key = header.label.replace(/\s+/g, '').toLowerCase();
      acc[key] = !defaultHiddenViewBookingColumns.includes(key);
      return acc;
    },
    {},
  );

  const [visibleColumns, setVisibleColumns] = useState<IVisibleColumns>(
    defaultVisibleColumns,
  );

  // Sync scroll between top bar and table
  useEffect(() => {
    if (tableRef && topScrollRef) {
      const handleMainScroll = () => {
        if (topScrollRef) {
          topScrollRef.scrollLeft = tableRef.scrollLeft;
        }
      };

      const handleTopScroll = () => {
        if (tableRef) {
          tableRef.scrollLeft = topScrollRef.scrollLeft;
        }
      };

      tableRef.addEventListener('scroll', handleMainScroll);
      topScrollRef.addEventListener('scroll', handleTopScroll);

      return () => {
        tableRef.removeEventListener('scroll', handleMainScroll);
        topScrollRef.removeEventListener('scroll', handleTopScroll);
      };
    }
  }, [tableRef, topScrollRef]);

  const handleFromDateChange = (date: Date | null) => {
    if (date) {
      setSelectedFromDate(date);
      if (validateDateRange(date, selectedToDate)) {
        setSelectedToDate(date);
      }
    }
  };
  const handleToDateChange = (date: Date | null) => {
    if (date) {
      setSelectedToDate(date);
    }
  };
  const onSetIsToggleColumns = () => {
    setIsToggleColumns(!isToggleColumns);
  };

  const ref = useClickOutside<HTMLDivElement>(() => {
    setIsToggleColumns(false);
  });

  const bookingsManifest = useCallback(
    async (
      toDate: Date,
      fromDate: Date,
      contactStaffId: number | null | undefined,
      ownerId: number,
    ) => {
      try {
        const encodedToDate = encodeDateForUrl(toDate);
        const encodedFromDate = encodeDateForUrl(fromDate);
        setIsPageLoading(true);
        const response = await viewBookingAPI.getBookingManifest(
          ownerId,
          contactStaffId,
          encodedToDate,
          encodedFromDate,
        );

        updateViewBooking(response?.data?.bookingsManifest);
      } catch (error) {
      } finally {
        setIsPageLoading(false);
      }
    },
    [],
  );

  const getAgents = useCallback(
    async (contactStaffId: number | null | undefined, ownerId: number) => {
      try {
        setIsPageLoading(true);
        const response = await viewBookingAPI.getAgents(
          ownerId,
          contactStaffId,
        );
        console.log('agent : ', response);
        setAgentList(response?.data);
      } catch (error) {
      } finally {
        setIsPageLoading(false);
      }
    },
    [],
  );

  const getTripStatus = useCallback(async (ownerId: number) => {
    try {
      setIsPageLoading(true);
      const response = await viewBookingAPI.getTripStatus(ownerId);
      console.log('agent : ', response);
      setTripStatusList(response?.data);
    } catch (error) {
    } finally {
      setIsPageLoading(false);
    }
  }, []);

  useEffect(() => {
    // TODO: all agent and status same for all owner
    getAgents(user?.id, selectedLocation.OwnerId);
    getTripStatus(selectedLocation.OwnerId);
  }, [getAgents, getTripStatus, user?.id]);

  const updateViewBooking = (bookingsManifest: any) => {
    setBooking(bookingsManifest);
    setViewBooking(bookingsManifest);
    setSearchQuery('');
  };

  const toggleColumn = (column: keyof IVisibleColumns) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const handleSearch = () => {
    if (!searchQuery?.trim()) {
      setViewBooking(booking);
      return;
    }
    const lowerCaseQuery = searchQuery?.toLowerCase()?.trim();

    // Iterate over bookingsManifest to filter relevant bookings
    const result = booking
      ?.map((manifest: any) => {
        const filteredBookings = manifest.bookings
          ?.map((bookingItem: any) => {
            const filteredDetails = bookingItem.bookingDetails?.filter(
              (detail: any) => {
                return (
                  detail.bookingId
                    .toString()
                    .toLowerCase()
                    .includes(lowerCaseQuery) ||
                  detail.tripDate?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.actualDate?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.paxName?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.status?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.tripName?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.refNo?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.agentName?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.pickup?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.dropoff?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.surveyStatus
                    ?.toLowerCase()
                    ?.includes(lowerCaseQuery) ||
                  detail.cxlSurveyStatus
                    ?.toLowerCase()
                    ?.includes(lowerCaseQuery) ||
                  detail.productName?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.bookedBy?.toLowerCase()?.includes(lowerCaseQuery) ||
                  detail.assocAgentName
                    ?.toLowerCase()
                    ?.includes(lowerCaseQuery) ||
                  detail.marketSource?.toLowerCase()?.includes(lowerCaseQuery)
                );
              },
            );

            if (filteredDetails && filteredDetails.length > 0) {
              return {
                ...bookingItem,
                bookingDetails: filteredDetails,
              };
            }
            return null;
          })
          .filter((booking: any) => booking !== null);

        if (filteredBookings.length > 0) {
          return {
            ...manifest,
            bookings: filteredBookings,
          };
        }
        return null;
      })
      .filter((manifest: any) => manifest !== null);

    setViewBooking(result);
  };

  const onChangeSearchQuery = (value: string) => {
    setSearchQuery(value);
    if (value.trim() === '') {
      setViewBooking(booking);
    }
  };

  useEffect(() => {
    bookingsManifest(
      selectedToDate,
      selectedFromDate,
      user?.id,
      selectedLocation.OwnerId,
    );
  }, [
    selectedToDate,
    selectedFromDate,
    bookingsManifest,
    selectedLocation,
    user,
  ]);

  const handleFilterChange = (
    selectedAgent: IAgent | null,
    selectedTripStatus: ITripStatus | null,
  ) => {
    let filteredData = booking;

    if (selectedAgent) {
      filteredData = filteredData
        .map((manifest: any) => {
          const filteredBookings = manifest.bookings
            .map((bookingItem: any) => {
              const filteredDetails = bookingItem.bookingDetails.filter(
                (detail: any) =>
                  detail.agentName
                    .toLowerCase()
                    .includes(selectedAgent.agentName.toLowerCase()),
              );

              return filteredDetails.length > 0
                ? { ...bookingItem, bookingDetails: filteredDetails }
                : null;
            })
            .filter(Boolean);

          return filteredBookings.length > 0
            ? { ...manifest, bookings: filteredBookings }
            : null;
        })
        .filter(Boolean);
    }

    if (selectedTripStatus) {
      filteredData = filteredData
        .map((manifest: any) => {
          const filteredBookings = manifest.bookings
            .map((bookingItem: any) => {
              const filteredDetails = bookingItem.bookingDetails.filter(
                (detail: any) =>
                  detail.tripStatusId === selectedTripStatus.tripStatusId,
              );

              return filteredDetails.length > 0
                ? { ...bookingItem, bookingDetails: filteredDetails }
                : null;
            })
            .filter(Boolean);

          return filteredBookings.length > 0
            ? { ...manifest, bookings: filteredBookings }
            : null;
        })
        .filter(Boolean);
    }

    setViewBooking(filteredData);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const columnDisplayNames: Record<string, string> = {
    date: 'Date',
    id: 'Booking ID',
    status: 'Status',
    name: 'Name',
    pretravel: 'Pre Travel',
    cxl: 'CXL',
    adult: 'Adult',
    child: 'Child',
    infant: 'Infant',
    d1opschecklist: 'D1 Ops Checklist',
    time: 'Time',
    pickup: 'Pickup',
    trip: 'Trip',
    dropoff: 'Drop Off',
    agent: 'Agent',
    person: 'Person',
    assocagent: 'Assoc Agent',
    marketsource: 'Market Source',
  };

  return (
    <div className={`bg-background px-6`}>
      <div className=" bg-background">
        <CalendarHeader />
      </div>
      <div className="text-start flex justify-between">
        <div>
          <h1 className="text-lg font-semibold text-textColorGray">
            {translate('viewBookings.viewBooking')}
          </h1>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-background mt-4 rounded-lg flex  items-end justify-between">
        <div className="flex z-20">
          <div className="text-start mr-4">
            <label className="text-textColor">{translate('common.from')}</label>
            <div className="flex items-center space-x-1 h-[38px] p-2 rounded border  border-customBorder-border bg-sidebar-background text-text-primary">
              <DateSelector
                onDateChange={handleFromDateChange}
                initialDate={selectedFromDate}
                showArrows={true}
              />
            </div>
          </div>
          <div className="text-start mr-4">
            <label className="text-textColor">{translate('common.to')}</label>
            <div className="flex items-center h-[38px] p-2 rounded border border-customBorder-border  bg-sidebar-background text-text-primary">
              <DateSelector
                onDateChange={handleToDateChange}
                initialDate={selectedToDate}
                showArrows={true}
              />
            </div>
          </div>
        </div>
        <div className="h-[38px] flex items-center w-full max-w-md border border-customBorder-border rounded-lg  px-4 shadow-sm focus-within:ring focus-within:ring-blue-300  bg-sidebar-background text-text-primary">
          <input
            type="text"
            value={searchQuery}
            placeholder="Search for Booking ID, Name, etc."
            className="flex-grow border-none bg-transparent focus:outline-none placeholder-gray-500 text-sm text-textColorGray  "
            onChange={(e) => onChangeSearchQuery(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          <button
            className="text-textColor hover:text-textColor"
            onClick={handleSearch}
          >
            <SearchIcon />
          </button>
        </div>
      </div>
      {/* Table Controls */}
      <div className="sticky z-10  bg-sidebar-background mt-4 pt-2">
        <div className={`flex justify-between items-center p-2 rounded-lg`}>
          <div className="flex">
            <div className="text-start mr-4">
              <ReusableAutocomplete<IAgent>
                options={agentList || ([] as IAgent[])}
                className="w-full text-sm  rounded-lg"
                getOptionLabel={(option: IAgent) => option.agentName}
                getOptionValue={(option: IAgent) => option.agentId}
                onChange={(selectedAgent) => {
                  setSelectedAgent(selectedAgent);
                  handleFilterChange(selectedAgent, selectedTripStatus);
                }}
                onChangeAgent={onChangeSearchQuery}
                placeholder="Agent"
              />
            </div>
            <div className="text-start mr-4  rounded-lg ">
              <ReusableAutocomplete<ITripStatus>
                options={tripStatus || ([] as ITripStatus[])}
                className="w-full text-sm "
                getOptionLabel={(option: ITripStatus) => option.description}
                getOptionValue={(option: ITripStatus) => option.tripStatusId}
                onChange={(selectedStatus) => {
                  setSelectedTripStatus(selectedStatus);
                  handleFilterChange(selectedAgent, selectedStatus);
                }}
                onChangeAgent={onChangeSearchQuery}
                placeholder="Status"
              />
            </div>
          </div>

          <div className="relative" ref={ref}>
            <ReusableButton
              variant="secondary"
              children={'Select Columns'}
              icon={
                <div>
                  <div className="block dark:hidden">
                    <AccordianDownIcon />
                  </div>
                  <div className="hidden dark:block">
                    <AccordianDownIconDark />
                  </div>
                </div>
              }
              textColor={'text-[#004E9B]'}
              onClick={onSetIsToggleColumns}
              className="bg-button-cancelButtonBgColor text-text-cancelButtonTextColor w-[195px]"
            />
            {isToggleColumns && (
              <div className="absolute right-0  bg-sidebar-background border -mt-2 border-slate-100 shadow-lg rounded z-10 overflow-y-auto max-h-64 w-[200px]">
                {(
                  Object.keys(visibleColumns)?.filter(
                    (column) =>
                      !viewBookingExcludedKeys.includes(column.toLowerCase()),
                  ) as (keyof IVisibleColumns)[]
                )?.map((column) => (
                  <label
                    key={column}
                    className="flex items-center px-4 py-2 space-x-1"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[column]}
                      onChange={() => toggleColumn(column)}
                    />
                    <span className="text-text-secondary">
                      {columnDisplayNames[column] ?? column}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      {/* Table Container with Fixed Header */}
      <div
        className={`${bgColor} p-4 sm:h-[440px] 2xl:h-[650px] flex flex-col`}
      >
        {/* Table container with fixed header */}
        <div className="relative flex-grow overflow-hidden">
          <div
            ref={setTableRef}
            className="overflow-auto max-h-full"
            style={{
              scrollbarGutter: 'stable both-edges',
              scrollbarWidth: 'thin',
              overflowY: 'scroll',
            }}
          >
            <table className="min-w-full border-collapse border border-customBorder-tableBorder shadow-lg table-fixed text-left">
              <thead className="bg-[#004E9B] text-text-tableHeading sticky top-0 z-9">
                <tr className="text-sm">
                  {headers
                    ?.filter(
                      (header) =>
                        visibleColumns[
                          header.label.replace(/\s+/g, '').toLowerCase()
                        ],
                    )
                    ?.map((header, index) => {
                      const setColumnWith = setWithColumn.includes(
                        header.label,
                      );
                      return (
                        <th
                          key={index}
                          className={`border border-slate-100 p-3 text-sm ${!setColumnWith ? ' min-w-28 w-[120px]' : ''}`}
                        >
                          {header.label}
                        </th>
                      );
                    })}
                </tr>
              </thead>
              <tbody>
                {isPageLoading ? (
                  <tr>
                    <td
                      colSpan={headers?.length || 1}
                      className="text-center p-4 h-[200px] text-lg font-semibold"
                    >
                      <div className="inline-grid">
                        <Loader width={50} height={50} />
                      </div>
                    </td>
                  </tr>
                ) : viewBooking?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={headers?.length || 1}
                      className="text-center p-4 h-[200px] text-lg font-semibold text-text-primary"
                    >
                      {translate('flightAllocation.noData')}
                    </td>
                  </tr>
                ) : (
                  viewBooking?.map((bookings: any, index: number) => (
                    <React.Fragment key={index}>
                      {/* Full-width row for date */}
                      <tr>
                        <td
                          colSpan={headers?.length || 1}
                          className={`text-left font-bold p-3 bg-[#E8F9F5]`}
                        >
                          {bookings?.date}
                        </td>
                      </tr>
                      {bookings?.bookings?.map(
                        (booking: any, index: number) => (
                          <React.Fragment key={index}>
                            {booking.bookingDetails?.map(
                              (detail: any, detailIndex: number) => (
                                <tr
                                  key={detail.bookingId + '-' + detailIndex}
                                  className={`${index % 2 === 0 ? 'bg-backgroundColor-white' : 'bg-gray-100'} text-text-primary`}
                                >
                                  {headers?.map((header, headerIndex) => {
                                    const filterKey = header.label
                                      ?.replace(/\s+/g, '')
                                      ?.toLowerCase();
                                    const rowKey = keyMapping[filterKey];
                                    if (!visibleColumns[filterKey]) return null;

                                    const rowData = rowKey
                                      ? (detail[
                                          rowKey as keyof typeof detail
                                        ] ?? '')
                                      : '';
                                    const shouldTruncate =
                                      truncateColumn.includes(filterKey);

                                    const pretravelLink =
                                      generatePretravelLink(detail);

                                    if (filterKey === allocation.DATE) {
                                      return (
                                        <td className="font-bold text-center align-middle border-gray-300 p-3"></td>
                                      );
                                    }

                                    if (filterKey === allocation.ID) {
                                      return (
                                        <td className="font-bold text-center align-middle border-gray-300 p-3">
                                          {detailIndex === 0 ? (
                                            booking.bookingId
                                          ) : (
                                            <span className="font-normal text-center">
                                              {
                                                booking?.bookingDetails?.[
                                                  detailIndex
                                                ]?.refNo
                                              }
                                            </span>
                                          )}
                                        </td>
                                      );
                                    }

                                    if (filterKey === allocation.PRETRAVEL) {
                                      return (
                                        <td
                                          key={headerIndex}
                                          dangerouslySetInnerHTML={{
                                            __html: pretravelLink,
                                          }}
                                        />
                                      );
                                    }

                                    if (filterKey === allocation.STATUS) {
                                      return (
                                        <td
                                          className={`font-medium  ${getStatusColorClass(rowData)}`}
                                        >
                                          {' '}
                                          {rowData}{' '}
                                        </td>
                                      );
                                    }

                                    if (filterKey === allocation.NAME) {
                                      return (
                                        <td
                                          className={`font-medium ${detail?.balance > 0 ? 'text-text-red' : ''}`}
                                        >
                                          {detailIndex === 0 ? (
                                            detail?.bookingName
                                          ) : (
                                            <span className="font-normal text-center"></span>
                                          )}
                                        </td>
                                      );
                                    }

                                    return (
                                      <td
                                        key={headerIndex}
                                        className={`border-gray-300 p-3 ${filterKey === allocation.AGENT ? ' min-w-48' : filterKey === allocation.NAME ? 'min-w-40' : ''}  ${
                                          shouldTruncate
                                            ? 'truncate max-w-xs cursor-pointer'
                                            : ''
                                        }`}
                                        title={
                                          shouldTruncate &&
                                          typeof rowData === 'string'
                                            ? rowData
                                            : undefined
                                        }
                                      >
                                        {shouldTruncate &&
                                        typeof rowData === 'string' &&
                                        rowData.length > 20
                                          ? `${rowData.substring(0, 20)}...`
                                          : rowData}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ),
                            )}
                          </React.Fragment>
                        ),
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingsDesktop;
